import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { agencyAPI, reviewsAPI, jobsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { INDUSTRIES, JOB_TYPES, JOB_ROLES, JOB_ROLE_CATEGORIES, EXPERIENCE_LEVELS, WORK_MODES, truncate } from '../../lib/utils';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { trackSearch, trackAgencyView, trackFilterUsed } from '../../lib/metaPixel';
import {
    Search,
    MapPin,
    Building2,
    Briefcase,
    Phone,
    Mail,
    Globe,
    Filter,
    X,
    Lock,
    Loader2,
    ChevronRight,
    Star,
    PoundSterling,
    ExternalLink,
    Eye,
    Crown,
    CheckCircle2,
    Sparkles
} from 'lucide-react';

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom purple marker icon
const purpleIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#7C3AED" width="32" height="32">
            <path d="M12 0C7.31 0 3.5 3.81 3.5 8.5c0 6.375 8.5 15.5 8.5 15.5s8.5-9.125 8.5-15.5C20.5 3.81 16.69 0 12 0zm0 12c-1.93 0-3.5-1.57-3.5-3.5S10.07 5 12 5s3.5 1.57 3.5 3.5S13.93 12 12 12z"/>
        </svg>
    `),
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
});

// Subscription Modal Component
const SubscriptionModal = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    
    if (!isOpen) return null;
    
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative animate-slide-up">
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 hover:bg-neutral-100 rounded-full"
                >
                    <X className="w-5 h-5 text-neutral-400" />
                </button>
                
                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Crown className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                        Create Your Free Account
                    </h2>
                    <p className="text-neutral-600">
                        Get direct contact with top recruitment agencies across the UK
                    </p>
                </div>
                
                <div className="space-y-3 mb-6">
                    {[
                        'View complete agency profiles & contact details',
                        'Direct phone & email access to recruiters',
                        'See all available job opportunities',
                        'Priority support from our team',
                        'Track your applications in one place'
                    ].map((benefit, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                            <span className="text-neutral-700">{benefit}</span>
                        </div>
                    ))}
                </div>
                
                <div className="bg-gradient-to-r from-emerald-50 to-primary-50 rounded-xl p-4 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-neutral-500">Price</p>
                            <p className="text-3xl font-bold text-emerald-700" data-testid="free-price-label">Free<span className="text-base font-normal text-neutral-500"> · no card needed</span></p>
                        </div>
                        <Sparkles className="w-10 h-10 text-primary-400" />
                    </div>
                </div>
                
                <div className="flex gap-3">
                    <Button 
                        variant="outline"
                        className="flex-1"
                        onClick={onClose}
                    >
                        Maybe Later
                    </Button>
                    <Button 
                        className="flex-1 bg-primary-600 hover:bg-primary-700"
                        onClick={() => navigate('/login')}
                        data-testid="signup-free-btn"
                    >
                        Sign Up Free
                    </Button>
                </div>
            </div>
        </div>
    );
};

const AgenciesPage = () => {
    const { isAuthenticated, login } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [agencies, setAgencies] = useState([]);
    const [matchingJobs, setMatchingJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingJobs, setLoadingJobs] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

    // Filter states
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [city, setCity] = useState(searchParams.get('city') || '');
    const [country, setCountry] = useState(searchParams.get('country') || '');  // New: Country filter
    const [industry, setIndustry] = useState(searchParams.get('industry') || '');
    const [jobType, setJobType] = useState(searchParams.get('job_type') || '');
    const [jobRole, setJobRole] = useState(searchParams.get('job_role') || '');
    const [experienceLevel, setExperienceLevel] = useState(searchParams.get('experience_level') || '');
    const [workMode, setWorkMode] = useState(searchParams.get('work_mode') || '');
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);

    // Selected agency for map
    const [selectedAgency, setSelectedAgency] = useState(null);

    // Map center - default to London
    const mapCenter = useMemo(() => {
        // Find first agency with valid coordinates
        const agencyWithCoords = agencies.find(a => a.latitude && a.longitude);
        if (agencyWithCoords) {
            return [agencyWithCoords.latitude, agencyWithCoords.longitude];
        }
        return [51.5074, -0.1278]; // London default
    }, [agencies]);

    // Filter agencies that have valid coordinates for map display
    const agenciesWithCoords = useMemo(() => {
        return agencies.filter(a => a.latitude && a.longitude);
    }, [agencies]);

    useEffect(() => {
        fetchAgencies();
    }, [page, country, industry, jobType, jobRole, experienceLevel, workMode]);

    // Fetch matching jobs when search term or job role changes
    useEffect(() => {
        const searchTerm = search || jobRole;
        if (searchTerm) {
            fetchMatchingJobs(searchTerm);
        } else {
            setMatchingJobs([]);
        }
    }, [search, jobRole]);

    const fetchMatchingJobs = async (searchTerm) => {
        if (!searchTerm) return;
        setLoadingJobs(true);
        try {
            // Search jobs by title matching the search term or job role
            const response = await jobsAPI.search({ search: searchTerm, limit: 6 });
            setMatchingJobs(response.data.jobs || []);
        } catch (error) {
            console.error('Failed to fetch matching jobs:', error);
            setMatchingJobs([]);
        } finally {
            setLoadingJobs(false);
        }
    };

    const fetchAgencies = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: 20,
                ...(search && { search }),
                ...(city && { city }),
                ...(country && { country }),  // Add country
                ...(industry && { industry }),
                ...(jobType && { job_type: jobType }),
                ...(jobRole && { job_role: jobRole }),
                ...(experienceLevel && { experience_level: experienceLevel }),
                ...(workMode && { work_mode: workMode }),
            };

            const response = await agencyAPI.list(params);
            // Defensive: legacy/imported records may have list fields stored as
            // comma-separated strings or null. Normalise so .map/.slice never crash.
            const LIST_FIELDS = ['industry', 'job_types', 'job_roles', 'experience_levels', 'work_modes', 'operating_cities'];
            const normalised = (response.data.agencies || []).map((a) => {
                const out = { ...a };
                LIST_FIELDS.forEach((f) => {
                    const v = out[f];
                    if (typeof v === 'string') {
                        out[f] = v.split(',').map((s) => s.trim()).filter(Boolean);
                    } else if (!Array.isArray(v)) {
                        out[f] = [];
                    }
                });
                return out;
            });
            setAgencies(normalised);
            setHasAccess(response.data.has_access);
            setTotalPages(response.data.total_pages);
            
            // Track search if there's a search term or filters
            if (search || city || industry || jobRole) {
                const searchTerm = search || city || industry || jobRole || '';
                trackSearch(searchTerm, 'agencies', response.data.agencies?.length || 0);
            }
        } catch (error) {
            console.error('Failed to fetch agencies:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchAgencies();
    };

    const clearFilters = () => {
        setSearch('');
        setCity('');
        setIndustry('');
        setJobType('');
        setJobRole('');
        setExperienceLevel('');
        setWorkMode('');
        setPage(1);
    };

    const handleViewDetails = (agency) => {
        if (hasAccess) {
            navigate(`/agencies/${agency.agency_id}`);
        } else {
            setShowSubscriptionModal(true);
        }
    };

    const hasActiveFilters = search || city || industry || jobType || jobRole || experienceLevel || workMode;

    return (
        <Layout hideFooter>
            <div className="h-[calc(100vh-64px)] flex flex-col lg:flex-row">
                {/* Left Panel - List */}
                <div className="w-full lg:w-[450px] xl:w-[500px] flex flex-col border-r border-neutral-200 bg-white">
                    {/* Search Header */}
                    <div className="p-4 border-b border-neutral-200">
                        <form onSubmit={handleSearch} className="space-y-3">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                                <Input
                                    placeholder="Search job role..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-12 h-12 rounded-xl"
                                    data-testid="search-agencies-input"
                                />
                            </div>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="City or postcode"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    className="h-10"
                                    data-testid="city-input"
                                />
                                <Button 
                                    type="submit" 
                                    className="bg-primary-600 hover:bg-primary-700"
                                    data-testid="search-btn"
                                >
                                    Search
                                </Button>
                                <Button 
                                    type="button" 
                                    variant="outline"
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={showFilters ? 'bg-primary-50' : ''}
                                    data-testid="filters-btn"
                                >
                                    <Filter className="w-4 h-4" />
                                </Button>
                            </div>
                        </form>

                        {/* Filters Panel */}
                        {showFilters && (
                            <div className="mt-4 p-4 bg-neutral-50 rounded-xl space-y-3 animate-fade-in">
                                {/* Country Filter - New */}
                                <Select value={country || 'all'} onValueChange={(v) => setCountry(v === 'all' ? '' : v)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="🌍 All Countries" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">🌍 All Countries</SelectItem>
                                        <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                                        <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                                    </SelectContent>
                                </Select>
                                
                                {/* Job Role Search - Full Width */}
                                <Select value={jobRole} onValueChange={setJobRole}>
                                    <SelectTrigger data-testid="job-role-filter">
                                        <SelectValue placeholder="Search by Job Role" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[300px]">
                                        {Object.entries(JOB_ROLE_CATEGORIES).map(([category, roles]) => (
                                            <div key={category}>
                                                <div className="px-2 py-1.5 text-xs font-semibold text-neutral-500 bg-neutral-100 sticky top-0">
                                                    {category}
                                                </div>
                                                {roles.map((role) => (
                                                    <SelectItem key={role} value={role} className="pl-4">{role}</SelectItem>
                                                ))}
                                            </div>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <div className="grid grid-cols-2 gap-3">
                                    <Select value={industry} onValueChange={setIndustry}>
                                        <SelectTrigger data-testid="industry-filter">
                                            <SelectValue placeholder="Industry" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {INDUSTRIES.map((ind) => (
                                                <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select value={jobType} onValueChange={setJobType}>
                                        <SelectTrigger data-testid="job-type-filter">
                                            <SelectValue placeholder="Job Type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {JOB_TYPES.map((type) => (
                                                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select value={experienceLevel} onValueChange={setExperienceLevel}>
                                        <SelectTrigger data-testid="experience-filter">
                                            <SelectValue placeholder="Experience" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {EXPERIENCE_LEVELS.map((level) => (
                                                <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select value={workMode} onValueChange={setWorkMode}>
                                        <SelectTrigger data-testid="work-mode-filter">
                                            <SelectValue placeholder="Work Mode" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {WORK_MODES.map((mode) => (
                                                <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {hasActiveFilters && (
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        onClick={clearFilters}
                                        className="text-neutral-500"
                                    >
                                        <X className="w-4 h-4 mr-1" />
                                        Clear filters
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Matching Jobs Section */}
                    {(search || jobRole) && matchingJobs.length > 0 && (
                        <div className="p-4 bg-emerald-50 border-b border-emerald-100">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                                    <Briefcase className="w-4 h-4" />
                                    Matching Jobs ({matchingJobs.length})
                                </h3>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-7 text-xs text-emerald-700 hover:text-emerald-900"
                                    onClick={() => navigate(`/jobs?search=${encodeURIComponent(search || jobRole)}`)}
                                >
                                    View All Jobs
                                    <ChevronRight className="w-3 h-3 ml-1" />
                                </Button>
                            </div>
                            <div className="space-y-2">
                                {matchingJobs.slice(0, 3).map((job) => (
                                    <div 
                                        key={job.job_id}
                                        className="p-3 bg-white rounded-lg border border-emerald-100 hover:border-emerald-300 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/jobs`)}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-medium text-neutral-900 text-sm truncate">
                                                    {job.title}
                                                </h4>
                                                <div className={`flex items-center gap-2 mt-1 text-xs ${!hasAccess ? 'blur-sm select-none' : 'text-neutral-600'}`}>
                                                    <span className="flex items-center gap-1">
                                                        <Building2 className="w-3 h-3" />
                                                        {job.agency_name}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <MapPin className="w-3 h-3" />
                                                        {job.location}
                                                    </span>
                                                </div>
                                            </div>
                                            <Badge className="text-xs bg-emerald-100 text-emerald-700 shrink-0">
                                                {job.job_type}
                                            </Badge>
                                        </div>
                                        {job.salary_min && (
                                            <div className={`flex items-center gap-1 mt-2 text-xs ${!hasAccess ? 'blur-sm select-none' : 'text-emerald-600 font-medium'}`}>
                                                <PoundSterling className="w-3 h-3" />
                                                {job.salary_min ? `From £${job.salary_min.toLocaleString()}` : 'Competitive'}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {matchingJobs.length > 3 && (
                                <p className="text-xs text-emerald-600 mt-2 text-center">
                                    +{matchingJobs.length - 3} more matching jobs
                                </p>
                            )}
                        </div>
                    )}

                    {/* Loading Jobs Indicator */}
                    {(search || jobRole) && loadingJobs && (
                        <div className="p-4 bg-emerald-50 border-b border-emerald-100 flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                            <span className="text-sm text-emerald-700">Finding matching jobs...</span>
                        </div>
                    )}

                    {/* Agency List */}
                    <div className="flex-1 overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                            </div>
                        ) : agencies.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 text-center p-4">
                                <Building2 className="w-12 h-12 text-neutral-300 mb-4" />
                                <p className="text-lg font-medium text-neutral-900">No agencies found</p>
                                <p className="text-sm text-neutral-500 mt-1">
                                    Try adjusting your search or filters
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-neutral-100">
                                {agencies.map((agency) => (
                                    <div
                                        key={agency.agency_id}
                                        className={`p-4 cursor-pointer transition-all hover:bg-neutral-50 ${
                                            selectedAgency?.agency_id === agency.agency_id ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                                        }`}
                                        onClick={() => setSelectedAgency(agency)}
                                        data-testid={`agency-card-${agency.agency_id}`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                                                <Building2 className="w-6 h-6 text-primary-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                {/* Agency Name - BLURRED for free users */}
                                                <h3 className={`font-semibold text-neutral-900 truncate ${!hasAccess ? 'blur-sm select-none' : ''}`}>
                                                    {agency.name}
                                                </h3>
                                                {/* Rating - Always visible */}
                                                {agency.average_rating > 0 && (
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <div className="flex">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <Star
                                                                    key={star}
                                                                    className={`w-3.5 h-3.5 ${
                                                                        star <= Math.round(agency.average_rating)
                                                                            ? 'text-amber-400 fill-amber-400'
                                                                            : 'text-neutral-300'
                                                                    }`}
                                                                />
                                                            ))}
                                                        </div>
                                                        <span className="text-xs text-neutral-500">
                                                            ({agency.review_count || 0})
                                                        </span>
                                                    </div>
                                                )}
                                                {/* City & Country */}
                                                <div className="flex items-center gap-1 text-sm text-neutral-500 mt-1">
                                                    <MapPin className="w-3.5 h-3.5" />
                                                    <span>{agency.city}</span>
                                                    <span className={!hasAccess ? 'blur-sm select-none' : ''}>, {agency.postcode}</span>
                                                    {agency.country && (
                                                        <span className="ml-2 text-xs px-2 py-0.5 bg-neutral-100 rounded">
                                                            {agency.country === 'Canada' ? '🇨🇦' : '🇬🇧'} {agency.country}
                                                        </span>
                                                    )}
                                                </div>
                                                {/* Operating Cities - Always visible */}
                                                {agency.operating_cities && agency.operating_cities.length > 0 && (
                                                    <div className="flex flex-wrap items-center gap-1 mt-1.5">
                                                        <span className="text-xs text-neutral-400">Also in:</span>
                                                        {agency.operating_cities.slice(0, 3).map((opCity) => (
                                                            <span key={opCity} className="text-xs px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">
                                                                {opCity}
                                                            </span>
                                                        ))}
                                                        {agency.operating_cities.length > 3 && (
                                                            <span className="text-xs text-neutral-400">+{agency.operating_cities.length - 3} more</span>
                                                        )}
                                                    </div>
                                                )}
                                                {/* Job Roles - Always visible with attractive styling */}
                                                {agency.job_roles && agency.job_roles.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-2">
                                                        <Briefcase className="w-3.5 h-3.5 text-primary-400 mr-0.5" />
                                                        {agency.job_roles.slice(0, 3).map((role) => (
                                                            <span key={role} className="text-xs px-2 py-0.5 bg-gradient-to-r from-primary-50 to-violet-50 text-primary-700 rounded-full font-medium">
                                                                {role}
                                                            </span>
                                                        ))}
                                                        {agency.job_roles.length > 3 && (
                                                            <span className="text-xs text-primary-500 font-medium">+{agency.job_roles.length - 3} more</span>
                                                        )}
                                                    </div>
                                                )}
                                                {/* Industries - Always visible */}
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {agency.industry?.slice(0, 2).map((ind) => (
                                                        <Badge key={ind} variant="secondary" className="text-xs">
                                                            {ind}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                            {/* View Details Button instead of Lock */}
                                            <Button
                                                size="sm"
                                                variant={hasAccess ? "outline" : "default"}
                                                className={`shrink-0 ${!hasAccess ? 'bg-primary-600 hover:bg-primary-700 text-white' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewDetails(agency);
                                                }}
                                            >
                                                <Eye className="w-4 h-4 mr-1" />
                                                View Details
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="p-4 border-t border-neutral-200 flex justify-between items-center">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page === 1}
                                onClick={() => setPage(page - 1)}
                            >
                                Previous
                            </Button>
                            <span className="text-sm text-neutral-500">
                                Page {page} of {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page === totalPages}
                                onClick={() => setPage(page + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </div>

                {/* Right Panel - Map (Hidden on Mobile, show agency details instead) */}
                <div className="hidden lg:flex flex-1 relative">
                    <MapContainer
                        center={mapCenter}
                        zoom={10}
                        className="w-full h-full"
                        scrollWheelZoom={true}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {agenciesWithCoords.map((agency) => (
                            <Marker
                                key={agency.agency_id}
                                position={[agency.latitude, agency.longitude]}
                                icon={purpleIcon}
                                eventHandlers={{
                                    click: () => setSelectedAgency(agency),
                                }}
                            >
                                <Popup>
                                    <div className="p-2 min-w-[200px]">
                                        <h3 className={`font-semibold text-neutral-900 ${!hasAccess ? 'blur-sm select-none' : ''}`}>{agency.name}</h3>
                                        <p className="text-sm text-neutral-500 mt-1">{agency.city}</p>
                                        {agency.operating_cities && agency.operating_cities.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {agency.operating_cities.slice(0, 2).map((opCity) => (
                                                    <span key={opCity} className="text-xs px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">
                                                        {opCity}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {/* Job Roles in Popup - Always visible */}
                                        {agency.job_roles && agency.job_roles.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {agency.job_roles.slice(0, 2).map((role) => (
                                                    <span key={role} className="text-xs px-1.5 py-0.5 bg-primary-50 text-primary-700 rounded">
                                                        {role}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {agency.industry?.slice(0, 2).map((ind) => (
                                                <Badge key={ind} variant="secondary" className="text-xs">
                                                    {ind}
                                                </Badge>
                                            ))}
                                        </div>
                                        <Button
                                            size="sm"
                                            className="w-full mt-3 bg-primary-600 hover:bg-primary-700"
                                            onClick={() => handleViewDetails(agency)}
                                        >
                                            <Eye className="w-3 h-3 mr-1" />
                                            View Details
                                        </Button>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>

                    {/* Selected Agency Card */}
                    {selectedAgency && (
                        <div className="absolute bottom-6 left-6 right-6 bg-white rounded-xl shadow-xl p-6 animate-slide-up">
                            <button
                                onClick={() => setSelectedAgency(null)}
                                className="absolute top-4 right-4 p-1 hover:bg-neutral-100 rounded"
                            >
                                <X className="w-5 h-5 text-neutral-400" />
                            </button>
                            <div className="flex items-start gap-4">
                                <div className="w-16 h-16 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                                    <Building2 className="w-8 h-8 text-primary-600" />
                                </div>
                                <div className="flex-1">
                                    <h3 className={`text-xl font-semibold text-neutral-900 ${!hasAccess ? 'blur-sm select-none' : ''}`}>
                                        {selectedAgency.name}
                                    </h3>
                                    {/* Rating in Selected Agency Card */}
                                    {selectedAgency.average_rating > 0 && (
                                        <div className="flex items-center gap-2 mt-1">
                                            <div className="flex">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <Star
                                                        key={star}
                                                        className={`w-4 h-4 ${
                                                            star <= Math.round(selectedAgency.average_rating)
                                                                ? 'text-amber-400 fill-amber-400'
                                                                : 'text-neutral-300'
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                            <span className="text-sm text-neutral-600">
                                                {selectedAgency.average_rating} ({selectedAgency.review_count || 0} reviews)
                                            </span>
                                        </div>
                                    )}
                                    <p className="text-neutral-500 mt-1">
                                        {selectedAgency.city}
                                        <span className={!hasAccess ? 'blur-sm select-none' : ''}>, {selectedAgency.postcode}</span>
                                    </p>
                                    {/* Operating Cities */}
                                    {selectedAgency.operating_cities && selectedAgency.operating_cities.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                            <span className="text-sm text-neutral-500">Also operates in:</span>
                                            {selectedAgency.operating_cities.map((opCity) => (
                                                <span key={opCity} className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full font-medium">
                                                    {opCity}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {/* Job Roles - Always visible */}
                                    {selectedAgency.job_roles && selectedAgency.job_roles.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                            <Briefcase className="w-4 h-4 text-primary-400" />
                                            {selectedAgency.job_roles.slice(0, 5).map((role) => (
                                                <span key={role} className="text-xs px-2 py-1 bg-gradient-to-r from-primary-50 to-violet-50 text-primary-700 rounded-full font-medium">
                                                    {role}
                                                </span>
                                            ))}
                                            {selectedAgency.job_roles.length > 5 && (
                                                <span className="text-xs text-primary-500 font-medium">+{selectedAgency.job_roles.length - 5} more</span>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        {selectedAgency.industry?.map((ind) => (
                                            <Badge key={ind} variant="secondary">{ind}</Badge>
                                        ))}
                                    </div>
                                    {/* Contact details - blurred for free users */}
                                    {hasAccess && (
                                        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                                            <a 
                                                href={`mailto:${selectedAgency.email}`}
                                                className="flex items-center gap-2 text-sm hover:text-primary-600 transition-colors"
                                            >
                                                <Mail className="w-4 h-4 text-primary-600" />
                                                <span className="underline truncate">{selectedAgency.email}</span>
                                            </a>
                                            <a 
                                                href={`tel:${selectedAgency.phone?.replace(/\s/g, '')}`}
                                                className="flex items-center gap-2 text-sm hover:text-primary-600 transition-colors"
                                            >
                                                <Phone className="w-4 h-4 text-primary-600" />
                                                <span className="underline">{selectedAgency.phone}</span>
                                            </a>
                                            <a 
                                                href={selectedAgency.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 text-sm hover:text-primary-600 transition-colors"
                                            >
                                                <Globe className="w-4 h-4 text-primary-600" />
                                                <span className="underline">Website</span>
                                            </a>
                                        </div>
                                    )}
                                    <div className="flex gap-3 mt-4">
                                        <Button 
                                            className="flex-1 bg-primary-600 hover:bg-primary-700"
                                            onClick={() => handleViewDetails(selectedAgency)}
                                        >
                                            <Eye className="w-4 h-4 mr-2" />
                                            {hasAccess ? 'View Full Profile' : 'View Details'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Mobile Agency Details Panel */}
                {selectedAgency && (
                    <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setSelectedAgency(null)}>
                        <div 
                            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-6 max-h-[80vh] overflow-y-auto animate-slide-up"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="w-12 h-1 bg-neutral-300 rounded-full mx-auto mb-4" />
                            <button
                                onClick={() => setSelectedAgency(null)}
                                className="absolute top-4 right-4 p-2 hover:bg-neutral-100 rounded-full"
                            >
                                <X className="w-5 h-5 text-neutral-400" />
                            </button>
                            
                            <div className="flex items-start gap-4 mb-4">
                                <div className="w-14 h-14 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                                    <Building2 className="w-7 h-7 text-primary-600" />
                                </div>
                                <div className="flex-1">
                                    <h3 className={`text-lg font-semibold text-neutral-900 ${!hasAccess ? 'blur-sm select-none' : ''}`}>
                                        {selectedAgency.name}
                                    </h3>
                                    {selectedAgency.average_rating > 0 && (
                                        <div className="flex items-center gap-1 mt-1">
                                            <div className="flex">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <Star
                                                        key={star}
                                                        className={`w-4 h-4 ${
                                                            star <= Math.round(selectedAgency.average_rating)
                                                                ? 'text-amber-400 fill-amber-400'
                                                                : 'text-neutral-300'
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                            <span className="text-xs text-neutral-500">({selectedAgency.review_count || 0})</span>
                                        </div>
                                    )}
                                    <p className="text-sm text-neutral-500 mt-1">
                                        {selectedAgency.city}
                                        <span className={!hasAccess ? 'blur-sm select-none' : ''}>, {selectedAgency.postcode}</span>
                                    </p>
                                </div>
                            </div>

                            {/* Job Roles */}
                            {selectedAgency.job_roles && selectedAgency.job_roles.length > 0 && (
                                <div className="mb-4">
                                    <p className="text-sm font-medium text-neutral-700 mb-2">Recruiting for:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedAgency.job_roles.map((role) => (
                                            <span key={role} className="text-xs px-2 py-1 bg-gradient-to-r from-primary-50 to-violet-50 text-primary-700 rounded-full font-medium">
                                                {role}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Industries */}
                            <div className="flex flex-wrap gap-2 mb-4">
                                {selectedAgency.industry?.map((ind) => (
                                    <Badge key={ind} variant="secondary">{ind}</Badge>
                                ))}
                            </div>

                            {/* Contact Info for subscribers */}
                            {hasAccess && (
                                <div className="space-y-3 mb-4 p-4 bg-neutral-50 rounded-xl">
                                    <a href={`tel:${selectedAgency.phone?.replace(/\s/g, '')}`} className="flex items-center gap-3 text-neutral-700">
                                        <Phone className="w-5 h-5 text-primary-600" />
                                        <span>{selectedAgency.phone}</span>
                                    </a>
                                    <a href={`mailto:${selectedAgency.email}`} className="flex items-center gap-3 text-neutral-700">
                                        <Mail className="w-5 h-5 text-primary-600" />
                                        <span className="truncate">{selectedAgency.email}</span>
                                    </a>
                                    <a href={selectedAgency.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-neutral-700">
                                        <Globe className="w-5 h-5 text-primary-600" />
                                        <span>Visit Website</span>
                                    </a>
                                </div>
                            )}

                            <Button 
                                className="w-full bg-primary-600 hover:bg-primary-700"
                                onClick={() => handleViewDetails(selectedAgency)}
                            >
                                <Eye className="w-4 h-4 mr-2" />
                                {hasAccess ? 'View Full Profile' : 'View Details'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Subscription Modal */}
            <SubscriptionModal 
                isOpen={showSubscriptionModal} 
                onClose={() => setShowSubscriptionModal(false)} 
            />
        </Layout>
    );
};

export default AgenciesPage;
